/**
 * Agrupación de insights por bloque.
 *
 * La API agrupa por **pregunta** (`InterviewInsight`), que es la unidad
 * comparable entre clientes. La vista de patrones necesita el nivel de arriba:
 * los cinco bloques del cuestionario, cada uno con sus tres preguntas y las
 * respuestas de todas las entrevistas. Eso es lo que arma este archivo.
 *
 * El orden lo manda el cuestionario del payload y no el orden en que la API
 * devolvió los insights: si mañana un bloque queda sin respuestas, tiene que
 * seguir apareciendo en su lugar y decir que está vacío. Un bloque que
 * desaparece se lee como un bloque que no existe.
 */
import type {
  InterviewBlock,
  InterviewBlockDef,
  InterviewInsight,
} from '@ops/contracts'

export interface BloqueDeInsights {
  bloque: InterviewBlock
  titulo: string
  intencion: string
  /** Las preguntas del bloque que tienen al menos una respuesta, en orden. */
  preguntas: InterviewInsight[]
  /** Respuestas del bloque, sumando sus preguntas. */
  respuestas: number
  /** Cuántas entrevistas distintas aportaron al menos una respuesta. */
  entrevistas: number
}

export function agruparPorBloque(
  insights: readonly InterviewInsight[],
  cuestionario: readonly InterviewBlockDef[],
): BloqueDeInsights[] {
  return cuestionario.map((def) => {
    const preguntas = insights
      .filter((insight) => insight.bloque === def.bloque)
      .sort((a, b) => a.preguntaNro - b.preguntaNro)

    const entrevistas = new Set<string>()
    let respuestas = 0
    for (const pregunta of preguntas) {
      respuestas += pregunta.respuestas.length
      for (const r of pregunta.respuestas) entrevistas.add(r.interviewId)
    }

    return {
      bloque: def.bloque,
      titulo: def.titulo,
      intencion: def.intencion,
      preguntas,
      respuestas,
      entrevistas: entrevistas.size,
    }
  })
}

/** Total de respuestas en todos los bloques. Para la bajada de la sección. */
export function totalRespuestas(bloques: readonly BloqueDeInsights[]): number {
  return bloques.reduce((suma, bloque) => suma + bloque.respuestas, 0)
}

/** Bloques que todavía nadie contestó: la lista de lo que no sabemos. */
export function bloquesVacios(
  bloques: readonly BloqueDeInsights[],
): BloqueDeInsights[] {
  return bloques.filter((bloque) => bloque.respuestas === 0)
}
